import { HexColorPicker } from "react-colorful";
import { useLocation, useNavigate } from "react-router-dom";
import { useApi } from "../../utils/Api";
import { Button, DialogActions, DialogContent, DialogTitle, InputLabel, Slider, Stack, TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import Translate from "../../utils/Translate";
import { useState } from "react";
import { useEffect } from "react";
import Preloader from "../Preloader";
import ButtonWithSpinner from "../ButtonWithSpinner";

const _defaultLicenseGroup = {
  webColor: "#ffffff",
  digitalLicenseColor: "#818589",
  digitalLicenseColorAlt: "#818589",
  primaryFontColor: "#ffffff",
  primaryFontColorAlt: "#ffffff",
  secondaryFontColor: "#ffffff80",
  secondaryFontColorAlt: "#ffffff80",
};

const _marks = [
  {
    value: 0,
    label: "0%",
  },
  {
    value: 20,
    label: "20%",
  },
  {
    value: 40,
    label: "40%",
  },
  {
    value: 60,
    label: "60%",
  },
  {
    value: 80,
    label: "80%",
  },
  {
    value: 100,
    label: "100%",
  },
];

function percentToHex(percentage) {
  if (percentage < 0 || percentage > 100) {
    throw new Error("Percentage must be between 0 and 100");
  }

  const decimalValue = Math.round((percentage / 100) * 255);
  let hexValue = decimalValue.toString(16).toUpperCase();
  if (hexValue.length === 1) {
    hexValue = "0" + hexValue;
  }
  return hexValue;
}

function hexToPercent(hex) {
  if (!/^[0-9A-Fa-f]{2}$/.test(hex)) {
    throw new Error(`Hex value must be a valid two-character hexadecimal value, not: ${hex}`);
  }

  const decimalValue = parseInt(hex, 16);
  const percentage = Math.round((decimalValue / 255) * 100 * 100) / 100;
  return percentage;
}

function getColor(colorAndOpacity) {
  return colorAndOpacity?.substring(0, 7);
}

function getOpacity(colorAndOpacity) {
  return !!colorAndOpacity && colorAndOpacity.length === 9 ? colorAndOpacity.substring(7) : null;
}

function ColorPicker({ title, colorName, licenseGroup, handleChangeColor, containerProps, hideTransparency }) {
  const currentColorAndOpacity = licenseGroup[colorName];
  const currentColor = getColor(currentColorAndOpacity);
  const currentOpacity = getOpacity(currentColorAndOpacity);
  return (
    <Stack direction="column" spacing={1} {...containerProps}>
      <InputLabel required>{title}</InputLabel>
      <HexColorPicker color={currentColor} onChange={(color) => handleChangeColor(colorName, color)} />
      <TextField
        sx={{ width: "200px" }}
        value={currentColor ?? ""}
        onChange={(ev) => handleChangeColor(colorName, ev.target.value)}
      />
      {!hideTransparency && (
        <Slider
          sx={{ width: "200px" }}
          value={currentColorAndOpacity && currentColorAndOpacity.length === 9 ? hexToPercent(currentOpacity) : 100}
          step={1}
          valueLabelDisplay="auto"
          marks={_marks}
          onChange={(_, newValue) => handleChangeColor(colorName, null, percentToHex(newValue))}
        />
      )}
    </Stack>
  );
}

export default function AddOrEditLicenseGroupDialog({ licenseGroupId }) {
  const [licenseGroup, setLicenseGroup] = useState(licenseGroupId ? null : _defaultLicenseGroup);
  const [isLoading, setIsLoading] = useState(true);

  const api = useApi();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (licenseGroupId) {
      api.fetch(`${process.env.REACT_APP_MAIN_URL}licenses/groups/${licenseGroupId}`, false, "GET").then((response) => {
        if (response.isSuccessful) {
          setLicenseGroup(response);
        }
      });
    }
    setIsLoading(false);
  }, [api, licenseGroupId]);

  const closeAndReload = () => {
    //Soft reload with redirect
    const hash = window.location.hash.endsWith("#dialog")
      ? window.location.hash.substring(0, window.location.hash.length - 7)
      : window.location.hash;
    const loc = encodeURIComponent(`${location.pathname}${location.search}${hash}`);
    navigate(`/redirect/${loc}`, { replace: true });
  };

  async function postLicenseGroup() {
    const response = await api.fetch(`${process.env.REACT_APP_MAIN_URL}licenses/groups`, licenseGroup, "POST");
    return response.isSuccessful;
  }

  async function handleSaveLicenseGroup() {
    if (await postLicenseGroup()) {
      closeAndReload();
    }
  }

  function handleChangeColor(colorName, newColor, newOpacity) {
    if (newColor && newColor.length != 7) throw new Error("Expected color without opacity");
    const currentColorAndOpacity = licenseGroup[colorName];
    const currentColor = getColor(currentColorAndOpacity);
    const currentOpacity = getOpacity(currentColorAndOpacity);
    const newColorAndOpacity = (newColor ?? currentColor) + (newOpacity ?? currentOpacity ?? "");
    setLicenseGroup((prev) => ({ ...prev, [colorName]: newColorAndOpacity }));
  }

  return (
    <>
      {isLoading && <Preloader />}
      {!isLoading && (
        <>
          <DialogContent className="stdDialogContent dialogOverlay">
            <br />
            <Stack direction="column" gap={4}>
              <TextField
                fullWidth
                value={licenseGroup?.name ?? ""}
                onChange={(event) => setLicenseGroup({ ...licenseGroup, name: event.target.value })}
                label={Translate.get("LicenseName")}
                placeholder={Translate.get("LicenseName")}
              />

              {licenseGroup && (
                <Stack direction="column" spacing={8} alignItems="stretch">
                  <ColorPicker
                    title={Translate.get("Color")}
                    colorName="webColor"
                    licenseGroup={licenseGroup}
                    handleChangeColor={handleChangeColor}
                    hideTransparency
                  />

                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent={{ xs: "flex-start", md: "space-between" }}
                    spacing={{ xs: 8, md: 3 }}
                  >
                    <ColorPicker
                      title={Translate.get("DigitalLicenseColor")}
                      colorName="digitalLicenseColor"
                      licenseGroup={licenseGroup}
                      handleChangeColor={handleChangeColor}
                      hideTransparency
                    />
                    <ColorPicker
                      title={Translate.get("DigitalLicenseColorAlt")}
                      colorName="digitalLicenseColorAlt"
                      licenseGroup={licenseGroup}
                      handleChangeColor={handleChangeColor}
                      containerProps={{ alignItems: { xs: "flex-start", md: "flex-end" } }}
                      hideTransparency
                    />
                  </Stack>

                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent={{ xs: "flex-start", md: "space-between" }}
                    spacing={{ xs: 8, md: 3 }}
                  >
                    <ColorPicker
                      title={Translate.get("DigitalLicensePrimaryFontColor")}
                      colorName="primaryFontColor"
                      licenseGroup={licenseGroup}
                      handleChangeColor={handleChangeColor}
                    />
                    <ColorPicker
                      title={Translate.get("DigitalLicensePrimaryFontColorAlt")}
                      colorName="primaryFontColorAlt"
                      licenseGroup={licenseGroup}
                      handleChangeColor={handleChangeColor}
                      containerProps={{ alignItems: { xs: "flex-start", md: "flex-end" } }}
                    />
                  </Stack>

                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent={{ xs: "flex-start", md: "space-between" }}
                    spacing={{ xs: 8, md: 3 }}
                  >
                    <ColorPicker
                      title={Translate.get("DigitalLicenseSecondaryFontColor")}
                      colorName="secondaryFontColor"
                      licenseGroup={licenseGroup}
                      handleChangeColor={handleChangeColor}
                    />
                    <ColorPicker
                      title={Translate.get("DigitalLicenseSecondaryFontColorAlt")}
                      colorName="secondaryFontColorAlt"
                      licenseGroup={licenseGroup}
                      handleChangeColor={handleChangeColor}
                      containerProps={{ alignItems: { xs: "flex-start", md: "flex-end" } }}
                    />
                  </Stack>
                </Stack>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeAndReload}>{Translate.get("Cancel")}</Button>
            <ButtonWithSpinner
              disabled={!licenseGroup || !licenseGroup.name || !licenseGroup.webColor}
              onClick={handleSaveLicenseGroup}
            >
              {Translate.get("Save")}
            </ButtonWithSpinner>
          </DialogActions>
        </>
      )}
    </>
  );
}
